import { useState } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { parseScheduleText } from '@bre-ticketing/domain';
import type { ApiError, CollectionRow } from '../../api';
import { eventEndpoints } from '../../api';
import { WEEKDAYS, seasonEndpoints } from '../../api-seasons';
import type { BuildInput, BuildPlan } from '../../api-seasons';
import { Badge, Button, EmptyRow, ErrorNote, Field, Panel, Table } from '../../ui';
import { INPUT } from '../event/shared';
import { chunks } from './QuickSelect';
import { dateWords } from '../../terms';

/**
 * Build a season from its schedule (D-371).
 *
 * Pick a game that is fully set up as the template, paste the schedule (or
 * load the league's CSV), and preview: every row with the game it would make
 * and anything wrong with it. Building is refused while a row has a problem,
 * so what was previewed is what is made. The API skips a game that already
 * exists, so building the same schedule twice makes nothing twice.
 *
 * Series (D-375): by default the games are grouped into one series per run
 * against the same team once they are built — asked for on the last part
 * only, so a run is never split between two requests. The template's series,
 * none, or one chosen series are still offered.
 */

const EXAMPLE = 'date, time, opponent, promo title, promo detail, promo sponsor, categories, featured\n2027-05-14, 7:05 PM, QUI, Fireworks Friday, Post-game fireworks, MidWest Bank, Fireworks, yes';

/** Rows sent per build request; the API allows 100, and a smaller group finishes well inside a request's limits. */
const BUILD_GROUP = 10;

export function BuildPanel({ season, onBuilt }: { season: CollectionRow; onBuilt: () => void }) {
  const events = useQuery({ queryKey: ['events', 'all'], queryFn: () => eventEndpoints.list() });
  const series = useQuery({ queryKey: ['collections', 'series'], queryFn: () => eventEndpoints.series() });
  const [templateId, setTemplateId] = useState('');
  const [seriesChoice, setSeriesChoice] = useState<string>('__group');
  const [pattern, setPattern] = useState('');
  const [keepCategories, setKeepCategories] = useState(true);
  const [createOpponents, setCreateOpponents] = useState(false);
  const [text, setText] = useState('');
  const [plan, setPlan] = useState<BuildPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);

  const input = (overrides: Partial<BuildInput> = {}): BuildInput => ({
    template_event_id: templateId,
    season_id: season.id,
    ...(seriesChoice === '__group' ? { group_by_opponent: true } : seriesChoice === '__template' ? { group_by_opponent: false } : { series_id: seriesChoice || null }),
    ...(pattern.trim() ? { title_pattern: pattern.trim() } : {}),
    keep_template_categories: keepCategories,
    create_opponents: createOpponents,
    text,
    dry_run: true,
    ...overrides,
  });

  const preview = useMutation({
    mutationFn: (overrides: Partial<BuildInput>) => seasonEndpoints.build(input(overrides)),
    onSuccess: (result) => {
      setPlan(result);
      setError(null);
    },
    onError: (cause: ApiError) => setError(cause.message),
  });

  const build = useMutation({
    mutationFn: async () => {
      // The pasted lines in groups; the header, if any, is dropped (the API skips it anyway).
      const lines = text.split(/\r?\n/).filter((line) => line.trim() && !/^\s*date\s*[,\t]/i.test(line));
      let made = 0;
      let series = 0;
      const parts = chunks(lines, BUILD_GROUP);
      for (const [i, group] of parts.entries()) {
        setProgress(`Building games ${i * BUILD_GROUP + 1}–${i * BUILD_GROUP + group.length} of ${lines.length}…`);
        // Grouping into series waits for the last part, so no run is split between two.
        const result = await seasonEndpoints.build(input({ text: group.join('\n'), dry_run: false, group_after_build: i === parts.length - 1 }));
        made += result.created ?? 0;
        series += result.series?.summary.create ?? 0;
      }
      return { made, series };
    },
    onSuccess: async ({ made, series }) => {
      setProgress(`${made} ${made === 1 ? 'game' : 'games'} made as drafts${series ? `, in ${series} new series by opponent` : ''}. Next: check their prices, then publish them.`);
      onBuilt();
      setPlan(await seasonEndpoints.build(input()));
    },
    onError: (cause: ApiError) => {
      setProgress(null);
      setError(cause.message);
    },
  });

  const templates = (events.data?.events ?? []).filter((event) => event.status !== 'ARCHIVED' && (event.performance_count ?? 0) > 0);
  const local = text.trim() ? parseScheduleText(text) : null;
  const unknownOpponents = plan?.rows.some((row) => row.problems.some((p) => p.code === 'OPPONENT_UNKNOWN')) ?? false;
  const canBuild = Boolean(plan && plan.summary.errors === 0 && plan.summary.create > 0);

  return (
    <div className="space-y-4">
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <Panel
        title="Build games from the schedule"
        description="Each row becomes a draft game copied from the template: its ticket types, price zones and seat map, prices (as a draft), fees, taxes, delivery, revenue split and page. The row sets the date and time, the opponent, the promotion and the categories. Nothing goes on sale until you publish."
      >
        <div className="grid gap-4 px-5 py-4 md:grid-cols-2">
          <Field label="Template game" htmlFor="build-template" hint={`A game that is fully set up. Its first ${dateWords().one} gives the venue, seating chart, timezone and opening time.`}>
            <select id="build-template" value={templateId} onChange={(e) => setTemplateId(e.target.value)} className={`w-full ${INPUT}`}>
              <option value="">Choose a game…</option>
              {templates.map((event) => (
                <option key={event.id} value={event.id}>
                  {event.title} ({event.slug})
                </option>
              ))}
            </select>
          </Field>
          <Field label="Series" htmlFor="build-series" hint="By opponent: consecutive games against one team become one series, e.g. vs Quincy · Jul 1–3.">
            <select id="build-series" value={seriesChoice} onChange={(e) => setSeriesChoice(e.target.value)} className={`w-full ${INPUT}`}>
              <option value="__group">Group into series by opponent</option>
              <option value="__template">The template&rsquo;s series</option>
              <option value="">None</option>
              {(series.data?.series ?? []).map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Title" htmlFor="build-pattern" hint="{opponent} is the team’s name, {short} its short name, {abbr} its abbreviation. Empty follows the template’s own title.">
            <input id="build-pattern" value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder={plan?.title_pattern ?? 'Bees vs. {opponent}'} maxLength={160} className={`w-full ${INPUT}`} />
          </Field>
          <div className="space-y-2 pt-5 text-sm">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={keepCategories} onChange={(e) => setKeepCategories(e.target.checked)} />
              Keep the template&rsquo;s categories on every game, and add each row&rsquo;s
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={createOpponents} onChange={(e) => setCreateOpponents(e.target.checked)} />
              Create opponents that are not on file yet
            </label>
          </div>
        </div>
        <div className="space-y-2 border-t border-line px-5 py-4">
          <Field label="Schedule" htmlFor="build-text" hint="One game per line: date, time, opponent (name, short name, abbreviation or web name), promo title, promo detail, promo sponsor, categories separated by semicolons, featured (yes/no). Commas or tabs; paste straight from a spreadsheet. The promo title names a promotion in the Promotions library (made there if it is new); a detail or sponsor that differs is kept for that game. A promotion with a rule needs no column: its rule picks up the games as they are built.">
            <textarea id="build-text" value={text} onChange={(e) => setText(e.target.value)} rows={10} placeholder={EXAMPLE} className={`w-full font-mono text-xs ${INPUT}`} />
          </Field>
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-sm text-ink-600">
              Or load a file{' '}
              <input
                type="file"
                accept=".csv,.tsv,.txt,text/csv,text/tab-separated-values,text/plain"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (file) setText(await file.text());
                }}
                className="text-sm"
              />
            </label>
            {local ? (
              <span className="text-xs text-ink-400">
                {local.rows.length} {local.rows.length === 1 ? 'game' : 'games'} read{local.problems.length ? `, ${local.problems.length} to fix` : ''}
              </span>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2 pt-1">
            <Button variant="primary" disabled={!templateId || !text.trim() || preview.isPending} onClick={() => preview.mutate({})}>
              {preview.isPending ? 'Checking…' : 'Preview'}
            </Button>
            <Button disabled={!canBuild || build.isPending} onClick={() => build.mutate()}>
              {build.isPending ? 'Building…' : plan ? `Build ${plan.summary.create} ${plan.summary.create === 1 ? 'game' : 'games'}` : 'Build'}
            </Button>
          </div>
          {progress ? <p className="text-sm text-ink-600">{progress}</p> : null}
        </div>
      </Panel>

      {plan ? (
        <Panel
          title={`Preview: ${plan.summary.create} to make, ${plan.summary.exists} already there, ${plan.summary.errors} to fix`}
          description={`Template ${plan.template.title}. Times are in ${plan.template.timezone}${plan.template.doors_lead_minutes !== null ? `; ${dateWords().gates.toLowerCase()} ${plan.template.doors_lead_minutes} minutes before, as on the template` : ''}.`}
          actions={
            unknownOpponents && !createOpponents ? (
              <Button
                onClick={() => {
                  setCreateOpponents(true);
                  preview.mutate({ create_opponents: true });
                }}
              >
                Create the new opponents too
              </Button>
            ) : null
          }
        >
          <Table headers={['Line', 'When', 'Game', 'Promotion', 'Categories', 'Status']}>
            {plan.rows.length === 0 ? (
              <EmptyRow columns={6}>No rows.</EmptyRow>
            ) : (
              plan.rows.map((row) => (
                <tr key={row.index} className="border-b border-line align-top last:border-0">
                  <td className="tabular px-5 py-2 text-ink-400">{row.line ?? row.index + 1}</td>
                  <td className="px-5 py-2 whitespace-nowrap">
                    {row.weekday !== null ? `${WEEKDAYS[row.weekday]} ` : ''}
                    {row.date} {row.time}
                  </td>
                  <td className="px-5 py-2">
                    <div className="text-ink-900">
                      {row.existing_event_id ? <Link to={`/events/${row.existing_event_id}`} className="hover:underline">{row.title ?? '—'}</Link> : (row.title ?? '—')}
                      {row.featured ? <span className="ml-1 text-xs text-ink-400">featured</span> : null}
                    </div>
                    {row.opponent?.create ? <Badge tone="accent">new opponent</Badge> : null}
                    {row.slug ? <div className="font-mono text-xs text-ink-400">/{row.slug}</div> : null}
                  </td>
                  <td className="px-5 py-2 text-ink-600">{row.promo_title ?? '—'}</td>
                  <td className="px-5 py-2 text-ink-600">{row.categories.map((c) => c.name).join(', ') || '—'}</td>
                  <td className="px-5 py-2">
                    <Badge tone={row.status === 'CREATE' ? 'ok' : row.status === 'EXISTS' ? 'neutral' : 'bad'}>{row.status === 'CREATE' ? 'Will make' : row.status === 'EXISTS' ? 'Already there' : 'Fix'}</Badge>
                    {row.event_id ? (
                      <Link to={`/events/${row.event_id}`} className="ml-2 text-xs text-brand hover:underline">
                        Open
                      </Link>
                    ) : null}
                    {[...row.problems, ...row.warnings].map((note) => (
                      <p key={note.code + note.message} className={`mt-1 text-xs ${row.problems.includes(note) ? 'text-bad' : 'text-ink-400'}`}>
                        {note.message}
                      </p>
                    ))}
                  </td>
                </tr>
              ))
            )}
          </Table>
        </Panel>
      ) : null}
    </div>
  );
}
